import { useMutation } from "@tanstack/react-query";
import { useDraftMode } from "../contexts/DraftModeContext";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { ChangesSource, ChangeType, NurseInShiftCreationSource } from "../utils/models";
import { usePublishChanges } from "./usePublishChanges";

export function useHandleRequest() {
	const { refreshDepartmentData, chosenDepartment } = useHospitalData();
	const { mutateAsync: publishChanges } = usePublishChanges();
	const { isDraftMode } = useDraftMode();

	const { mutateAsync: decline } = useMutation({
		mutationFn: async ({ requestId, nurseIds }: { requestId: string; nurseIds: number[] }) => {
			return await publishChanges({
				accepted_requests: {},
				declined_requests: { [requestId]: nurseIds },
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			});
		},
		onSuccess: (response) => {
			if (response.shouldRefresh) {
				refreshDepartmentData(chosenDepartment.department_id, false);
			}
		},
	});

	const {
		mutateAsync: replaceRequest,
		isPending: isReplacing,
		error: replaceRequestError,
	} = useMutation({
		mutationFn: async ({ nurseToAdd, nurseToRemove, requestId, shiftIndex, dayNo, weekIndex, date, accepted_start_time, accepted_end_time }: any) => {
			const changes = [
				{
					nurse: {
						...nurseToRemove,
						isPending: false,
					},
					shiftIndex,
					shiftType: nurseToRemove.shiftType,
					weekIndex,
					dayIndex: dayNo,
					date,
					departmentId: nurseToRemove.departmentId,
					changeType: ChangeType.Delete,
				},
				{
					nurse: {
						...nurseToAdd,
						isPending: false,
					},
					shiftIndex,
					shiftType: nurseToAdd.shiftType,
					weekIndex,
					dayIndex: dayNo,
					date,
					departmentId: nurseToAdd.departmentId,
					changeType: ChangeType.Insert,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes,
				source: ChangesSource.Staffing,
				creationSource: NurseInShiftCreationSource.Requests,
				accepted_start_time: accepted_start_time,
				accepted_end_time: accepted_end_time,
			};

			if (requestId) {
				updateRequestBody.accepted_requests = {
					[requestId]: [nurseToAdd.nurseId],
				};
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);

				if (response.shouldRefresh) {
					refreshDepartmentData(nurseToRemove.departmentId, false);
				}

				return response;
			}
			return { shouldRefresh: false };
		},
	});

	return {
		decline,
		replaceRequest,
		isLoading: {
			replaceRequest: isReplacing,
		},
		error: {
			replaceRequest: replaceRequestError,
		},
	};
}
